<template>
  <el-dialog
    title="选择儿童"
    :visible.sync="dialogVisible"
    width="900px"
    :close-on-click-modal="false"
    @close="handleClose"
    append-to-body
  >
    <div class="form-section">
      <el-button
        type="primary"
        size="small"
        class="mb10"
        @click="handleAddChild"
        >新增参会儿童</el-button
      >
      <el-table
        ref="childTable"
        :data="candidates"
        border
        size="small"
        :max-height="420"
        @selection-change="handleSelectionChange"
        style="width: 100%"
      >
        <el-table-column
          type="selection"
          width="50"
          :selectable="checkSelectable"
        />
        <el-table-column label="姓名" prop="realName" align="center" />
        <el-table-column label="性别" prop="sex" align="center">
          <template slot-scope="{ row }">
            {{ row.sex === 1 ? "男" : row.sex === 2 ? "女" : "" }}
          </template>
        </el-table-column>
        <el-table-column
          label="与参会成人关系"
          prop="relationship"
          align="center"
        />
        <el-table-column
          label="身份证号码"
          prop="idCardNo"
          align="center"
          width="200px"
        >
          <template slot-scope="{ row }">
            <span>{{ row.idCardNo }}</span>
            <span v-if="row.isChild" style="color: #999; margin-left: 5px;"
              >(儿童)</span
            >
            <span v-else style="color: #999; margin-left: 5px;">(成人)</span>
          </template>
        </el-table-column>
        <el-table-column label="手机号" prop="mobile" align="center" />
        <el-table-column label="操作" align="center" width="100">
          <template slot-scope="{ $index }">
            <el-button type="text" @click="handleEdit($index)">编辑</el-button>
          </template>
        </el-table-column>
      </el-table>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button @click="handleCancel">取消</el-button>
      <el-button type="primary" @click="handleConfirm">确认</el-button>
    </span>

    <!-- 新增/编辑儿童表单弹窗 -->
    <person-form-dialog
      :visible.sync="personFormVisible"
      :title="personFormTitle"
      :form-data="editingPerson"
      :user-id="userId"
      :user-info-id="userInfoId"
      :user-type="userType"
      @confirm="handlePersonFormConfirm"
    />
  </el-dialog>
</template>

<script>
import PersonFormDialog from "./PersonFormDialog.vue";
import meetingCarryPeopleApi from "@/api/meeting/meetingCarryPeople";

export default {
  name: "SelectChildDialog",
  components: {
    PersonFormDialog
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    meetingId: {
      type: [Number, String],
      default: null
    },
    userId: {
      type: [Number, String],
      default: null
    },
    userInfoId: {
      type: [Number, String],
      default: null
    },
    userType: {
      type: Number,
      default: null
    },
    maxCarryChildPer: {
      type: Number,
      default: 0
    },
    existingChildren: {
      type: Array,
      default: () => []
    }
  },
  data() {
    return {
      dialogVisible: this.visible,
      candidates: [],
      selectedRows: [],
      personFormVisible: false,
      personFormTitle: "新建参会儿童",
      editingPerson: {},
      editingIndex: null
    };
  },
  watch: {
    visible(newVal) {
      this.dialogVisible = newVal;
      if (newVal) {
        this.loadCandidates();
      }
    },
    dialogVisible(newVal) {
      this.$emit("update:visible", newVal);
    }
  },
  methods: {
    // 格式化身份证显示
    formatIdCard(idCard) {
      if (!idCard) return "";
      if (idCard.length <= 8) return idCard;
      const start = idCard.substring(0, 3);
      const end = idCard.substring(idCard.length - 4);
      return `${start}${"*".repeat(idCard.length - 7)}${end}`;
    },
    // 加载候选列表
    async loadCandidates() {
      this.selectedRows = [];
      try {
        const res = await meetingCarryPeopleApi.list({
          pageNo: 1,
          pageSize: 100,
          isChild: true,
          userId: this.userId,
          meetingId: this.meetingId,
          userInfoId: this.userInfoId,
          userType: this.userType
        });
        const list =
          (res &&
            (res.data && res.data.list ? res.data.list : res.data || res)) ||
          [];
        const mapped = (list || [])
          .filter(it => it.isChild)
          .map(it => ({
            realName: it.realName || "",
            sex: it.sex,
            relationship: it.relationship || "",
            idCardNo: it.idCardNo || "",
            mobile: it.mobile || "",
            isChild: true,
            userId: it.userId || null,
            userInfoId: it.userInfoId || null,
            userType: it.userType || null
          }));
        this.candidates = mapped;
        
        // 根据 existingChildren 回显已选择的儿童
        this.restoreSelectedChildren();
      } catch (e) {
        this.candidates = [];
      }
    },
    // 恢复已选择的儿童（回显）
    restoreSelectedChildren() {
      if (!this.existingChildren || this.existingChildren.length === 0) {
        return;
      }
      
      // 根据 existingChildren 匹配 candidates，找到对应的行
      const matchedRows = [];
      this.existingChildren.forEach(existing => {
        const candidate = this.candidates.find(c => {
          // 使用 idCardNo + realName 匹配（儿童通常没有 id）
          return c.idCardNo === existing.idCardNo && c.realName === existing.realName;
        });
        
        if (candidate) {
          matchedRows.push(candidate);
        }
      });
      
      // 设置 selectedRows
      this.selectedRows = matchedRows;
      
      // 在下一个 tick 勾选表格中的行
      this.$nextTick(() => {
        if (this.$refs.childTable && matchedRows.length > 0) {
          matchedRows.forEach(row => {
            this.$refs.childTable.toggleRowSelection(row, true);
          });
        }
      });
    },
    // 检查行是否可选
    checkSelectable(row) {
      // 如果没有设置 maxCarryChildPer 或为 0，不限制
      if (!this.maxCarryChildPer || this.maxCarryChildPer === 0) {
        return true;
      }

      // 计算当前已选择的儿童数量
      const selectedCount = this.selectedRows.length;

      // 如果当前行已经是选中的，则允许取消选择
      const isSelected = this.selectedRows.some(
        r => r.idCardNo === row.idCardNo && r.realName === row.realName
      );
      if (isSelected) {
        return true;
      }

      // 如果已选择的儿童数量已达到限制，则不允许再选择
      return selectedCount < this.maxCarryChildPer;
    },
    // 选择变化
    handleSelectionChange(rows) {
      // 如果设置了 maxCarryChildPer 限制
      if (this.maxCarryChildPer && this.maxCarryChildPer > 0) {
        // 检查是否超过限制
        if (rows.length > this.maxCarryChildPer) {
          this.$message.warning(`最多只能选择 ${this.maxCarryChildPer} 个儿童`);
          // 恢复到之前的选择状态
          this.$nextTick(() => {
            // 先清空所有选择
            this.$refs.childTable.clearSelection();
            // 然后恢复之前的选择
            this.selectedRows.forEach(row => {
              const rowIndex = this.candidates.findIndex(
                c => c.idCardNo === row.idCardNo && c.realName === row.realName
              );
              if (rowIndex >= 0) {
                this.$refs.childTable.toggleRowSelection(
                  this.candidates[rowIndex],
                  true
                );
              }
            });
          });
          return;
        }
      }
      this.selectedRows = rows;
    },
    // 打开新增弹窗
    handleAddChild() {
      this.personFormTitle = "新建参会儿童";
      this.editingPerson = {
        isChild: true,
        userId: this.userId || null,
        userInfoId: this.userInfoId || null,
        userType: this.userType || null
      };
      this.editingIndex = null;
      this.personFormVisible = true;
    },
    // 打开编辑弹窗
    handleEdit(index) {
      this.personFormTitle = "编辑参会儿童";
      this.editingPerson = { ...this.candidates[index] };
      this.editingIndex = index;
      this.personFormVisible = true;
    },
    // 表单确认（新增或编辑时调用接口保存）
    async handlePersonFormConfirm(personData) {
      try {
        // 构建接口所需的数据结构
        const apiData = {
          meetingId: this.meetingId || null,
          realName: personData.realName || "",
          mobile: personData.mobile || "",
          sex: personData.sex || null,
          relationship: personData.relationship || "",
          idCardNo: personData.idCardNo || "",
          isChild: true, // 儿童标识
          userId: personData.userId || this.userId || null,
          userInfoId: personData.userInfoId || this.userInfoId || null,
          userType: personData.userType || this.userType || null
        };

        // 调用接口保存
        await meetingCarryPeopleApi.create(apiData);

        // 保存成功后，添加到列表或更新列表，并关闭弹窗
        if (this.editingIndex !== null) {
          // 编辑模式：更新现有数据
          this.$set(this.candidates, this.editingIndex, personData);
          this.$message.success("编辑参会儿童成功");
        } else {
          // 新增模式：添加到列表
          this.candidates.push(personData);
          this.$message.success("新增参会儿童成功");
        }
        // 保存成功，关闭表单弹窗
        this.personFormVisible = false;
      } catch (error) {
        console.error("保存参会儿童失败:", error);
        this.$message.error(
          error.msg ||
            (this.editingIndex !== null
              ? "编辑参会儿童失败"
              : "新增参会儿童失败")
        );
        // 保存失败，不关闭弹窗，让用户修改后重试
      }
    },
    // 关闭弹窗
    handleClose() {
      this.selectedRows = [];
      this.candidates = [];
      this.$emit("close");
    },
    // 取消
    handleCancel() {
      this.dialogVisible = false;
    },
    // 确认
    handleConfirm() {
      this.$emit("confirm", this.selectedRows);
      this.dialogVisible = false;
    }
  }
};
</script>

<style lang="scss" scoped>
.form-section {
  padding: 20px;
  border-radius: 10px;
  background-color: #fff;
  margin-bottom: 10px;
}
</style>
